import { createSlice } from "@reduxjs/toolkit";
import { CsvReportNameEnum, CsvJobNameKeyEnum, EARNINGS_ANALYTICS_CSV_NAME_PREFIX } from "./enums";
import { csvReportsApi } from "./endpoints";
import {
  TeamUsersCsvT,
  OrgProjectsCsvT,
  TeamAmbassadorsCsvT,
  EarningsAnalyticsCsvT,
  TeamIntelligenceCsvT,
} from "./types";

const resolveCsvStateKey = (name: string): CsvReportNameEnum | null => {
  if (name.startsWith(EARNINGS_ANALYTICS_CSV_NAME_PREFIX)) {
    return CsvReportNameEnum.EARNINGS_ANALYTICS;
  }
  if ((Object.values(CsvReportNameEnum) as string[]).includes(name)) {
    return name as CsvReportNameEnum;
  }
  return null;
};

export type CsvReportsReduxStateT = {
  [CsvReportNameEnum.TEAM_USERS]: TeamUsersCsvT;
  [CsvReportNameEnum.ORG_TEAM_USERS]: TeamUsersCsvT;
  [CsvReportNameEnum.TEAM_AMBASSADORS]: TeamAmbassadorsCsvT;
  [CsvReportNameEnum.ORG_TEAM_SEATED_USERS]: TeamUsersCsvT;
  [CsvReportNameEnum.ORG_PRE_SALES_PROJECTS]: OrgProjectsCsvT;
  [CsvReportNameEnum.ORG_POST_SALES_PROJECTS]: OrgProjectsCsvT;
  [CsvReportNameEnum.ORG_COMPLETED_PROJECTS]: OrgProjectsCsvT;
  [CsvReportNameEnum.EARNINGS_ANALYTICS]: EarningsAnalyticsCsvT;
  [CsvReportNameEnum.TEAM_INTELLIGENCE]: TeamIntelligenceCsvT;
};

const csvJobInitialState = <T>(jobTitle: CsvJobNameKeyEnum, reportName: CsvReportNameEnum): T => {
  return {
    createdAt: "",
    id: 0,
    jobName: null,
    jobStatus: null,
    jobs: {
      [jobTitle]: {
        results: false,
        state: null,
        ts: "",
      },
    },
    name: reportName,
    url: null,
    updatedAt: "",
    userId: 0,
  } as T;
};

const initialState: CsvReportsReduxStateT = {
  [CsvReportNameEnum.TEAM_USERS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.TEAM_USERS,
  ),
  [CsvReportNameEnum.ORG_TEAM_USERS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.ORG_TEAM_USERS,
  ),
  [CsvReportNameEnum.TEAM_AMBASSADORS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.TEAM_AMBASSADORS,
  ),
  [CsvReportNameEnum.ORG_TEAM_SEATED_USERS]: csvJobInitialState<TeamUsersCsvT>(
    CsvJobNameKeyEnum.TEAM_USERS,
    CsvReportNameEnum.ORG_TEAM_SEATED_USERS,
  ),
  [CsvReportNameEnum.ORG_PRE_SALES_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_PRE_SALES_PROJECTS,
  ),
  [CsvReportNameEnum.ORG_POST_SALES_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_POST_SALES_PROJECTS,
  ),
  [CsvReportNameEnum.ORG_COMPLETED_PROJECTS]: csvJobInitialState<OrgProjectsCsvT>(
    CsvJobNameKeyEnum.PROJECTS,
    CsvReportNameEnum.ORG_COMPLETED_PROJECTS,
  ),
  [CsvReportNameEnum.EARNINGS_ANALYTICS]: csvJobInitialState<EarningsAnalyticsCsvT>(
    CsvJobNameKeyEnum.EARNINGS_ANALYTICS,
    CsvReportNameEnum.EARNINGS_ANALYTICS,
  ),
  [CsvReportNameEnum.TEAM_INTELLIGENCE]: csvJobInitialState<TeamIntelligenceCsvT>(
    CsvJobNameKeyEnum.TEAM_INTELLIGENCE,
    CsvReportNameEnum.TEAM_INTELLIGENCE,
  ),
};

export const csvReportsSlice = createSlice({
  name: "csvReports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(csvReportsApi.endpoints?.getTeamUsersCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getOrgProjectsCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getAmbassadorsCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getEarningsAnalyticsCsv.matchFulfilled, (state, { payload }) => {
      state[CsvReportNameEnum.EARNINGS_ANALYTICS] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getTeamIntelligenceCsv.matchFulfilled, (state, { payload }) => {
      state[payload.csv.name] = payload.csv;
    });
    builder.addMatcher(csvReportsApi.endpoints?.getCsvReportById.matchFulfilled, (state, { payload }) => {
      const key = resolveCsvStateKey(payload.report.name);
      if (key) {
        state[key] = { ...state[key], ...payload.report };
      }
    });
  },
  selectors: {
    selectTeamUsersCsv: (state) => state[CsvReportNameEnum.TEAM_USERS],
    selectOrgTeamUsersCsv: (state) => state[CsvReportNameEnum.ORG_TEAM_USERS],
    selectOrgTeamSeatedUsersCsv: (state) => state[CsvReportNameEnum.ORG_TEAM_SEATED_USERS],
    selectTeamAmbassadorsCsv: (state) => state[CsvReportNameEnum.TEAM_AMBASSADORS],
    selectOrgPreSalesCsv: (state) => state[CsvReportNameEnum.ORG_PRE_SALES_PROJECTS],
    selectOrgPostSalesCsv: (state) => state[CsvReportNameEnum.ORG_POST_SALES_PROJECTS],
    selectOrgCompletedCsv: (state) => state[CsvReportNameEnum.ORG_COMPLETED_PROJECTS],
    selectEarningsAnalyticsCsv: (state) => state[CsvReportNameEnum.EARNINGS_ANALYTICS],
    selectTeamIntelligenceCsv: (state) => state[CsvReportNameEnum.TEAM_INTELLIGENCE],
  },
});

export const {
  selectTeamUsersCsv,
  selectOrgTeamUsersCsv,
  selectOrgTeamSeatedUsersCsv,
  selectOrgPreSalesCsv,
  selectOrgPostSalesCsv,
  selectOrgCompletedCsv,
  selectTeamAmbassadorsCsv,
  selectEarningsAnalyticsCsv,
  selectTeamIntelligenceCsv,
} = csvReportsSlice.selectors;

export default csvReportsSlice;
